import { Ingredient, isIngredientReal } from '@cf-shared/data-model/ingredient';
import { MealTunerIngredient } from '@cf-shared/meal-tuner-interface';
import { deepClone } from '@cf-shared/deep-helpers';

/** What the rebuild produced, plus whether it could trust the two lists to line up. */
export interface MealTunerRebuildResult {
  /** The meal's new food rows: one per source row, in the same order. */
  ingredients: Ingredient[];
  /**
   * True when the two lists were different lengths, so row `i` of the answer is NOT
   * necessarily row `i` of the source. The rebuild does not guess in that case: `ingredients`
   * is then the source list copied unchanged, with no tuning applied, and the caller is
   * expected to report the mismatch rather than treat the meal as tuned.
   */
  positionsMismatched: boolean;
}

/**
 * Build a meal's new food rows out of the meal tuner's answer.
 *
 * A meal is a LIST of food rows, not a set of foods: the same food can appear twice, and
 * every hand-typed "Quick Macros" row is handed to the tuner under one shared placeholder
 * food id (`MACRO_INGREDIENT_FOOD_ID`), because it has no food in the database to name it.
 * So a food id cannot tell one row from another, and matching on it deleted the user's food
 * three different ways (#4535): a typed-in row was skipped outright, two helpings of one
 * food collapsed into one, and anything the tuner did not answer with was simply gone.
 *
 * The tuner answers with one row per row it was given, in the same order, and never filters
 * (`solveOneMeal` maps over the list it is handed). So this walks the two lists by POSITION:
 * each source row is carried through as it was, with only its weight replaced by the tuned
 * one. Nothing is looked up by food id and nothing is skipped.
 *
 * The tuner itself already works this way on its own side, for the same reason -- see the
 * comment on `modelKeyForIngredient` in `shared/src/meal-tuner.ts`. This is the way back out.
 *
 * The source rows are deep-cloned, so the caller's own list is never modified.
 *
 * Only a REAL row is re-weighed. The tuner does not answer with a weight for a hand-typed row:
 * it sets that row's `tunedMeasures` to 1, meaning "one whole typed-in block", because the row's
 * absolute nutrients were handed over separately (`meal-tuner.ts`, the macro branch above the
 * locked branch). Multiplying that 1 by the row's `gramsPerMeasure`, which is always 1 for a
 * typed-in row, would rewrite a 100 g row as 1 g -- so a typed-in row keeps the weight the user
 * gave it.
 */
export function rebuildMealIngredientsFromTuner(
  sourceIngredients: Ingredient[],
  tunedIngredients: MealTunerIngredient[],
): MealTunerRebuildResult {
  // if the two lists are not the same length, position no longer identifies a row. say so, and
  // hand back the source untouched, so a caller that ignores the flag leaves the meal as it was
  // rather than emptying it.
  if (sourceIngredients.length !== tunedIngredients.length) {
    return {
      ingredients: sourceIngredients.map(ingredient => deepClone(ingredient)),
      positionsMismatched: true,
    };
  }

  const ingredients: Ingredient[] = [];
  for (let i = 0; i < sourceIngredients.length; i++) {
    const tunedIngredient = tunedIngredients[i];
    const ingredient = deepClone(sourceIngredients[i]);

    // if this is a real food, take the tuned weight; a hand-typed row keeps the user's own
    // weight, because the tuner answers 1 for it rather than a weight (see the note above).
    if (isIngredientReal(ingredient)) {
      ingredient.weight_g = tunedIngredient.tunedMeasures * tunedIngredient.gramsPerMeasure;
      ingredient.locked = tunedIngredient.locked;
    }

    // kcal is only a cached number for quick display, and it was cached for the weight this
    // row USED to have. Drop it rather than carry it: recalculateMealNutrientsAsync fills it
    // in again from the new weight, and showing nothing beats showing the old meal figure.
    ingredient.kcal = undefined;

    ingredients.push(ingredient);
  }

  return { ingredients, positionsMismatched: false };
}
